import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getAdminId } from '../../utils/getAdminId';
import { StickyNote, Plus, Pin, Trash2, Edit3, X, Check } from 'lucide-react';
import { format } from 'date-fns';

type NoteColor = 'yellow' | 'pink' | 'mint' | 'blue' | 'peach';

interface DashboardNote {
  id: string;
  admin_id: string;
  user_id: string;
  content: string;
  color: NoteColor;
  pinned: boolean;
  created_at: string;
  updated_at: string;
  user_name?: string;
}

const NOTE_COLORS = {
  yellow: {
    bg: 'bg-sticky-yellow dark:bg-amber-800',
    bgLight: 'bg-sticky-yellow-light dark:bg-amber-900',
    border: 'border-sticky-yellow-dark/30 dark:border-amber-600/30',
    text: 'text-amber-900 dark:text-amber-100',
    meta: 'text-amber-700/70 dark:text-amber-300/70',
  },
  pink: {
    bg: 'bg-sticky-pink dark:bg-pink-800',
    bgLight: 'bg-sticky-pink-light dark:bg-pink-900',
    border: 'border-sticky-pink-dark/30 dark:border-pink-600/30',
    text: 'text-pink-900 dark:text-pink-100',
    meta: 'text-pink-700/70 dark:text-pink-300/70',
  },
  mint: {
    bg: 'bg-sticky-mint dark:bg-emerald-800',
    bgLight: 'bg-sticky-mint-light dark:bg-emerald-900',
    border: 'border-sticky-mint-dark/30 dark:border-emerald-600/30',
    text: 'text-emerald-900 dark:text-emerald-100',
    meta: 'text-emerald-700/70 dark:text-emerald-300/70',
  },
  blue: {
    bg: 'bg-sticky-blue dark:bg-blue-800',
    bgLight: 'bg-sticky-blue-light dark:bg-blue-900',
    border: 'border-sticky-blue-dark/30 dark:border-blue-600/30',
    text: 'text-blue-900 dark:text-blue-100',
    meta: 'text-blue-700/70 dark:text-blue-300/70',
  },
  peach: {
    bg: 'bg-sticky-peach dark:bg-orange-800',
    bgLight: 'bg-sticky-peach-light dark:bg-orange-900',
    border: 'border-sticky-peach-dark/30 dark:border-orange-600/30',
    text: 'text-orange-900 dark:text-orange-100',
    meta: 'text-orange-700/70 dark:text-orange-300/70',
  },
};

const ROTATIONS = ['-rotate-1', 'rotate-0', 'rotate-1', '-rotate-2', 'rotate-2'];

export const NotesWidget: React.FC = () => {
  const { user } = useAuth();
  const [notes, setNotes] = useState<DashboardNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [newNote, setNewNote] = useState<{ content: string; color: NoteColor; pinned: boolean }>({ content: '', color: 'yellow', pinned: false });
  const [editContent, setEditContent] = useState('');

  const adminId = getAdminId(user);

  const loadNotes = useCallback(async () => {
    if (!adminId) return;

    try {
      const { data, error } = await supabase
        .from('dashboard_notes')
        .select(`
          id,
          admin_id,
          user_id,
          content,
          color,
          pinned,
          created_at,
          updated_at
        `)
        .eq('admin_id', adminId)
        .order('pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      const notesWithUserNames = await Promise.all(
        (data || []).map(async (note) => {
          const { data: userData } = await supabase
            .from('users')
            .select('name')
            .eq('id', note.user_id)
            .maybeSingle();
          return { ...note, user_name: userData?.name || 'Unknown' };
        })
      );

      setNotes(notesWithUserNames);
    } catch (error) {
      console.error('Error loading notes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [adminId]);

  useEffect(() => {
    loadNotes();

    const subscription = adminId
      ? supabase
          .channel(`dashboard_notes_changes_${adminId}`)
          .on('postgres_changes',
            { event: '*', schema: 'public', table: 'dashboard_notes', filter: `admin_id=eq.${adminId}` },
            () => {
              loadNotes();
            }
          )
          .subscribe()
      : null;

    return () => {
      if (subscription) subscription.unsubscribe();
    };
  }, [loadNotes, adminId]);

  const handleAddNote = async () => {
    if (!newNote.content.trim() || !user?.id || !adminId) return;

    try {
      const { error } = await supabase
        .from('dashboard_notes')
        .insert({
          admin_id: adminId,
          user_id: user.id,
          content: newNote.content.trim(),
          color: newNote.color,
          pinned: newNote.pinned,
        });

      if (error) throw error;

      setNewNote({ content: '', color: 'yellow', pinned: false });
      setShowAddForm(false);
      loadNotes();
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const handleUpdateNote = async (noteId: string) => {
    if (!editContent.trim()) return;

    try {
      const { error } = await supabase
        .from('dashboard_notes')
        .update({ content: editContent.trim() })
        .eq('id', noteId);

      if (error) throw error;

      setEditingNote(null);
      setEditContent('');
      loadNotes();
    } catch (error) {
      console.error('Error updating note:', error);
    }
  };

  const handleTogglePin = async (noteId: string, currentPinned: boolean) => {
    try {
      const { error } = await supabase
        .from('dashboard_notes')
        .update({ pinned: !currentPinned })
        .eq('id', noteId);

      if (error) throw error;
      loadNotes();
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Delete this note?')) return;

    try {
      const { error } = await supabase
        .from('dashboard_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;
      loadNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  const startEditing = (note: DashboardNote) => {
    setEditingNote(note.id);
    setEditContent(note.content);
  };

  if (isLoading) {
    return (
      <div className="card p-6 h-[400px] flex flex-col overflow-hidden">
        <div className="flex items-center gap-2 mb-4">
          <StickyNote className="h-5 w-5 text-amber-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Team Notes</h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6 h-[400px] flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <StickyNote className="h-5 w-5 text-amber-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Team Notes</h3>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
          title="Add note"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {showAddForm && (
        <div className={`mb-4 p-4 rounded-xl ${NOTE_COLORS[newNote.color].bg} sticky-note sticky-note-shadow animate-note-appear`}>
          <div className="sticky-note-tape dark:opacity-50" />
          <textarea
            value={newNote.content}
            onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
            placeholder="Write your note..."
            className={`w-full bg-transparent border-none resize-none focus:outline-none font-handwriting text-xl ${NOTE_COLORS[newNote.color].text} placeholder-current/50 min-h-[80px]`}
            autoFocus
          />
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-black/10 dark:border-white/10">
            <div className="flex items-center gap-2">
              {(['yellow', 'pink', 'mint', 'blue', 'peach'] as NoteColor[]).map((color) => (
                <button
                  key={color}
                  onClick={() => setNewNote({ ...newNote, color })}
                  className={`w-6 h-6 rounded-full ${NOTE_COLORS[color].bg} border-2 transition-transform hover:scale-110 ${
                    newNote.color === color ? 'ring-2 ring-offset-1 ring-gray-600 dark:ring-gray-300 scale-110' : 'border-white/50 dark:border-black/30'
                  }`}
                  title={color}
                />
              ))}
              <button
                onClick={() => setNewNote({ ...newNote, pinned: !newNote.pinned })}
                className={`ml-2 p-1.5 rounded-lg transition-colors ${
                  newNote.pinned
                    ? 'bg-gray-800 dark:bg-white text-white dark:text-gray-800'
                    : 'bg-black/10 dark:bg-white/20 text-gray-700 dark:text-gray-200 hover:bg-black/20 dark:hover:bg-white/30'
                }`}
                title={newNote.pinned ? 'Pinned' : 'Pin note'}
              >
                <Pin className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewNote({ content: '', color: 'yellow', pinned: false });
                }}
                className="p-1.5 rounded-lg bg-black/10 dark:bg-white/20 text-gray-700 dark:text-gray-200 hover:bg-black/20 dark:hover:bg-white/30 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
              <button
                onClick={handleAddNote}
                disabled={!newNote.content.trim()}
                className="p-1.5 rounded-lg bg-gray-800 dark:bg-white text-white dark:text-gray-800 hover:bg-gray-900 dark:hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {notes.length === 0 && !showAddForm ? (
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 mb-3">
            <StickyNote className="h-8 w-8 text-amber-500" />
          </div>
          <p className="text-gray-500 dark:text-gray-400 font-medium">No notes yet</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Jot something down!</p>
        </div>
      ) : (
        <div className="flex-1 space-y-3 overflow-y-auto pr-1">
          {notes.map((note, index) => {
            const colorStyle = NOTE_COLORS[note.color] || NOTE_COLORS.yellow;
            const rotation = ROTATIONS[index % ROTATIONS.length];
            const isEditing = editingNote === note.id;

            return (
              <div
                key={note.id}
                className={`relative p-4 rounded-xl ${colorStyle.bg} sticky-note sticky-note-shadow ${rotation} transition-all duration-200 hover:rotate-0 hover:scale-[1.02] group`}
              >
                <div className="sticky-note-tape dark:opacity-50" />

                {note.pinned && (
                  <div className="absolute top-2 right-2">
                    <Pin className="h-4 w-4 text-gray-600/60 dark:text-gray-300/60 fill-current" />
                  </div>
                )}

                {isEditing ? (
                  <div>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className={`w-full bg-transparent border-none resize-none focus:outline-none font-handwriting text-xl ${colorStyle.text} min-h-[60px]`}
                      autoFocus
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <button
                        onClick={() => setEditingNote(null)}
                        className="p-1.5 rounded-lg bg-black/10 dark:bg-white/20 hover:bg-black/20 dark:hover:bg-white/30 transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleUpdateNote(note.id)}
                        className="p-1.5 rounded-lg bg-black/20 dark:bg-white/30 hover:bg-black/30 dark:hover:bg-white/40 transition-colors"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className={`font-handwriting text-xl ${colorStyle.text} leading-relaxed whitespace-pre-wrap break-words pr-6`}>
                      {note.content}
                    </p>
                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-black/10 dark:border-white/10">
                      <span className={`text-xs font-sans ${colorStyle.meta}`}>
                        {note.user_name} - {format(new Date(note.created_at), 'MMM d')}
                      </span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleTogglePin(note.id, note.pinned)}
                          className={`p-1 rounded hover:bg-black/10 dark:hover:bg-white/20 transition-colors ${note.pinned ? 'text-gray-700 dark:text-gray-200' : 'text-gray-500 dark:text-gray-300'}`}
                          title={note.pinned ? 'Unpin' : 'Pin'}
                        >
                          <Pin className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => startEditing(note)}
                          className="p-1 rounded text-gray-500 dark:text-gray-300 hover:bg-black/10 dark:hover:bg-white/20 transition-colors"
                          title="Edit"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="p-1 rounded text-gray-500 dark:text-gray-300 hover:bg-black/10 dark:hover:bg-white/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
